import {
  buildXrhubAppRedirectUrl,
  buildXrhubLoginRedirectUrl,
  resolveXrhubBaseUrl
} from "../../utils/xrhubRedirect.js";

const INSTANT_ACCESS_SID_PATTERN = /^[A-Za-z0-9]{7}$/;
const AUTH_CALLBACK_PARAMS = ["code", "state", "error", "error_description"];

function resolveCurrentAbsoluteUrl(location) {
  const pathname = location?.pathname || "/";
  const search = location?.search || "";
  const hash = location?.hash || "";
  const origin = location?.origin || "";

  return origin ? new URL(`${pathname}${search}${hash}`, origin).href : `${pathname}${search}${hash}`;
}

function normalizeCreatorBasePath(creatorBasePath) {
  const normalizedBasePath = String(creatorBasePath || "")
    .trim()
    .replace(/\/+$/, "");

  if (!normalizedBasePath || normalizedBasePath === "/") {
    return "";
  }

  return normalizedBasePath.startsWith("/") ? normalizedBasePath : `/${normalizedBasePath}`;
}

function normalizeAuth0Redirect(auth0Redirect) {
  return String(auth0Redirect || "").trim();
}

function sanitizeAuthCallbackSearch(search = "") {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));

  AUTH_CALLBACK_PARAMS.forEach(param => params.delete(param));

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function resolveLocalAuthRedirectUri(location, { auth0Redirect, creatorBasePath } = {}) {
  const normalizedAuth0Redirect = normalizeAuth0Redirect(auth0Redirect);

  if (normalizedAuth0Redirect) {
    return new URL(normalizedAuth0Redirect, location?.origin || window.location.origin).href;
  }

  return `${location?.origin || window.location.origin}${normalizeCreatorBasePath(creatorBasePath)}`;
}

export function buildEditorLoginRedirectUrl(location, { xrhubUrl } = {}) {
  const returnTo = resolveCurrentAbsoluteUrl(location);

  return buildXrhubLoginRedirectUrl(returnTo, {
    xrhubUrl,
    locationOrigin: location?.origin || ""
  });
}

export function buildLocalEditorReturnToPath(location, { creatorBasePath } = {}) {
  const pathname = location?.pathname || normalizeCreatorBasePath(creatorBasePath) || "/";
  const search = sanitizeAuthCallbackSearch(location?.search || "");
  const hash = location?.hash || "";

  return `${pathname}${search}${hash}`;
}

export async function handoffToEditorLogin({
  location,
  xrhubUrl,
  creatorBasePath = "",
  auth0Redirect = "",
  useLocalLogin = false,
  loginWithRedirect = null,
  persistReturnTo = () => {},
  navigate = url => window.location.replace(url)
} = {}) {
  const normalizedCreatorBasePath = normalizeCreatorBasePath(creatorBasePath);

  if (useLocalLogin && typeof loginWithRedirect === "function") {
    const returnTo = buildLocalEditorReturnToPath(location, {
      creatorBasePath: normalizedCreatorBasePath
    });

    persistReturnTo(returnTo);

    await loginWithRedirect({
      appState: {
        returnTo
      },
      authorizationParams: {
        redirect_uri: resolveLocalAuthRedirectUri(location, {
          auth0Redirect,
          creatorBasePath: normalizedCreatorBasePath
        })
      }
    });

    return {
      mode: "local",
      returnTo
    };
  }

  const redirectUrl = buildEditorLoginRedirectUrl(location, {
    xrhubUrl
  });
  navigate(redirectUrl);

  return {
    mode: "xrhub",
    redirectUrl
  };
}

function normalizeLogoutRedirectPath(redirectPath) {
  const normalizedPath = String(redirectPath || "").trim();

  if (!normalizedPath || !normalizedPath.startsWith("/") || normalizedPath.startsWith("//")) {
    return "";
  }

  return normalizedPath;
}

export function buildEditorLogoutRedirectUrl({ redirectPath, xrhubUrl, locationOrigin, creatorBasePath } = {}) {
  const safeRedirectPath = normalizeLogoutRedirectPath(redirectPath);
  const logoutPath = safeRedirectPath ? `/logout?redirect=${encodeURIComponent(safeRedirectPath)}` : "/logout";

  return buildXrhubAppRedirectUrl(logoutPath, {
    xrhubUrl,
    locationOrigin,
    creatorBasePath
  });
}

export function buildInstantAccessRedirectUrl({ instantAccessSid, xrhubUrl, locationOrigin, creatorBasePath } = {}) {
  const hasValidInstantSid = typeof instantAccessSid === "string" && INSTANT_ACCESS_SID_PATTERN.test(instantAccessSid);
  const reentryPath = hasValidInstantSid ? `/instant/${instantAccessSid}` : "/pin-login";

  return buildXrhubAppRedirectUrl(`${creatorBasePath || ""}${reentryPath}`, {
    xrhubUrl,
    locationOrigin,
    creatorBasePath
  });
}

export function isAllowedPostLogoutRedirectUrl(redirectUrl, { locationOrigin, xrhubUrl } = {}) {
  if (!redirectUrl) {
    return false;
  }

  try {
    const candidateUrl = new URL(redirectUrl, locationOrigin || "");
    const allowedOrigins = new Set();

    if (locationOrigin) {
      allowedOrigins.add(new URL(locationOrigin).origin);
    }

    const xrhubBaseUrl = resolveXrhubBaseUrl(xrhubUrl, { locationOrigin });
    if (xrhubBaseUrl) {
      allowedOrigins.add(new URL(xrhubBaseUrl).origin);
    }

    return allowedOrigins.has(candidateUrl.origin);
  } catch (_error) {
    return false;
  }
}
